import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { apiGet, apiPost } from '../../api/client.js';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { PLANS, PLAN_TIER_ORDER } from '../../lib/plans.js';

// ─── AI credits wallet + top-up (ADR-0031) ──────────────────────────────────
//
// Shows the org's credit balance and monthly allotment, a low/exhausted
// banner, and a pack picker that starts a one-time Stripe Checkout. When
// credits run out, content still ships (reused/stock imagery) — the copy makes
// that clear so "out of credits" doesn't read as "broken".

function CreditsCard() {
  const creditsQ = useQuery({ queryKey: ['billing-credits'], queryFn: () => apiGet('/billing/credits') });
  const packsQ = useQuery({ queryKey: ['credit-packs'], queryFn: () => apiGet('/billing/credits/packs') });
  const [buyErr, setBuyErr] = useState(null);

  const buy = useMutation({
    mutationFn: (pack) => apiPost('/billing/credits/checkout', { pack }),
    onSuccess: (res) => { if (res?.url) window.location.assign(res.url); },
    onError: (err) => setBuyErr(err.body?.detail || err.body?.error || err.message),
  });

  const c = creditsQ.data;
  if (creditsQ.isLoading || !c) return null;
  if (c.unlimited) {
    return (
      <div className="billing-status-card" style={{ marginTop: 24 }}>
        <h3 style={{ marginTop: 0 }}>AI credits</h3>
        <p className="muted small">This account has unlimited AI credits.</p>
      </div>
    );
  }

  const pct = c.monthly_allotment > 0 ? Math.max(0, Math.min(100, Math.round((c.plan_remaining / c.monthly_allotment) * 100))) : 0;
  const packs = packsQ.data?.packs || [];

  return (
    <div className="billing-status-card" style={{ marginTop: 24 }}>
      <h3 style={{ marginTop: 0 }}>AI credits</h3>

      {c.exhausted && (
        <div className="billing-warn" style={{ marginBottom: 12 }}>
          You're out of AI credits this month. Your posts keep going out on schedule using your
          brand's existing images — top up to switch fresh AI generation back on, or it resumes
          automatically next month.
        </div>
      )}
      {!c.exhausted && c.low && (
        <div className="post-action-msg warn" style={{ marginBottom: 12 }}>
          Running low on AI credits. When they run out, posts fall back to your brand's existing
          images until you top up or the monthly allotment resets.
        </div>
      )}

      <div className="billing-status-row">
        <span className="billing-label">Balance</span>
        <span className="billing-value"><b>{c.balance.toLocaleString()}</b> credits</span>
      </div>
      <div className="quota-bars" style={{ marginTop: 8 }}>
        <QuotaBar label={`Monthly allotment (${c.plan_remaining.toLocaleString()} of ${c.monthly_allotment.toLocaleString()} left)`}
          used={c.monthly_allotment - c.plan_remaining} cap={c.monthly_allotment} pct={100 - pct} />
      </div>
      {c.topup > 0 && (
        <div className="quota-stat-row">
          <span className="billing-label">Purchased credits (roll over)</span>
          <span>{c.topup.toLocaleString()}</span>
        </div>
      )}

      <hr />
      <h4 style={{ margin: '4px 0 10px' }}>Buy more credits</h4>
      <p className="muted small" style={{ marginTop: 0 }}>
        One-time purchase, no subscription change. Purchased credits never expire.
      </p>
      {buyErr && <div className="post-action-msg err" style={{ marginBottom: 10 }}>{buyErr}</div>}
      <div className="credit-pack-grid">
        {packs.map((p) => (
          <button key={p.key} type="button" className="credit-pack-card"
            disabled={buy.isPending}
            onClick={() => { setBuyErr(null); buy.mutate(p.key); }}>
            <span className="credit-pack-credits">{p.credits.toLocaleString()} credits</span>
            <span className="credit-pack-price">${p.price_usd}</span>
          </button>
        ))}
        {!packs.length && <span className="muted small">Credit packs aren't available yet.</span>}
      </div>
    </div>
  );
}

// ─── Usage bar (posts + AI calls) ────────────────────────────────────────────

function QuotaBar({ label, used, cap, pct }) {
  const warn   = pct >= 80;
  const danger = pct >= 95;
  return (
    <div className="quota-bar-row">
      <div className="quota-bar-header">
        <span className="billing-label">{label}</span>
        <span className={`quota-bar-count ${danger ? 'danger' : warn ? 'warn' : ''}`}>
          {used} / {cap}
        </span>
      </div>
      <div className="quota-bar-track">
        <div
          className={`quota-bar-fill ${danger ? 'danger' : warn ? 'warn' : ''}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── Upgrade confirm modal ────────────────────────────────────────────────────

function UpgradeConfirmModal({ pending, isPending, onConfirm, onCancel, currentStatus }) {
  if (!pending) return null;
  const priceLabel = pending.cadence === 'annual'
    ? `$${pending.priceAnnual}/yr`
    : `$${pending.priceMonthly}/mo`;

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3 className="modal-title">Upgrade to {pending.name}?</h3>
        <p className="modal-body">
          {currentStatus === 'trialing'
            ? `You're currently on a free trial. Upgrading to ${pending.name} (${priceLabel}) will set the post-trial price — no extra charge today.`
            : `You'll be pro-rated for the remainder of the current billing period and charged the difference for ${pending.name} (${priceLabel}). The new plan limits take effect immediately.`}
        </p>
        <div className="modal-actions">
          <button className="btn" onClick={onCancel} disabled={isPending}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Upgrading…' : `Upgrade to ${pending.name}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Billing() {
  const { organization } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [busy, setBusy]           = useState(null);
  const [error, setError]         = useState(null);
  const [toast, setToast]         = useState(null);
  const [cadence, setCadence]     = useState('monthly');
  const [pendingUpgrade, setPendingUpgrade] = useState(null);

  // ── Auto-trigger checkout when arriving via landing "Get started" ──
  // URL shape: /account/billing?plan=pro&cadence=monthly
  const [autoCheckoutFired, setAutoCheckoutFired] = useState(false);
  useEffect(() => {
    if (autoCheckoutFired) return;
    const planParam    = searchParams.get('plan');
    const cadenceParam = searchParams.get('cadence');
    const VALID = ['solo', 'pro', 'agency'];
    if (planParam && VALID.includes(planParam)) {
      setAutoCheckoutFired(true);
      searchParams.delete('plan');
      searchParams.delete('cadence');
      setSearchParams(searchParams, { replace: true });
      if (cadenceParam === 'monthly' || cadenceParam === 'annual') setCadence(cadenceParam);
      setTimeout(() => {
        checkout.mutate({ plan_tier: planParam, billing_cadence: cadenceParam || 'monthly' });
        setBusy(`${planParam}-${cadenceParam || 'monthly'}`);
      }, 400);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Handle Stripe Checkout return ──
  useEffect(() => {
    const status = searchParams.get('status');
    if (status === 'success') {
      setToast({ kind: 'ok', text: '🎉 Subscription activated — welcome aboard!' });
      queryClient.invalidateQueries({ queryKey: ['billing-status'] });
      searchParams.delete('status');
      searchParams.delete('session');
      setSearchParams(searchParams, { replace: true });
      const t = setTimeout(() => setToast(null), 8000);
      return () => clearTimeout(t);
    }
    if (status === 'cancel') {
      setToast({ kind: 'warn', text: 'Checkout cancelled — no charge was made.' });
      searchParams.delete('status');
      setSearchParams(searchParams, { replace: true });
      const t = setTimeout(() => setToast(null), 6000);
      return () => clearTimeout(t);
    }
    // Credit top-up return (ADR-0031). The webhook grants the credits; poll the
    // wallet a moment later so the new balance shows without a manual refresh.
    const credits = searchParams.get('credits');
    if (credits === 'purchased') {
      setToast({ kind: 'ok', text: '🎉 Credits added to your account.' });
      searchParams.delete('credits');
      searchParams.delete('session');
      setSearchParams(searchParams, { replace: true });
      setTimeout(() => queryClient.invalidateQueries({ queryKey: ['billing-credits'] }), 1500);
      const t = setTimeout(() => setToast(null), 8000);
      return () => clearTimeout(t);
    }
    if (credits === 'cancel') {
      setToast({ kind: 'warn', text: 'Credit purchase cancelled — no charge was made.' });
      searchParams.delete('credits');
      setSearchParams(searchParams, { replace: true });
      const t = setTimeout(() => setToast(null), 6000);
      return () => clearTimeout(t);
    }
  }, [searchParams, setSearchParams, queryClient]);

  // ── Billing status query ──
  const { data, isLoading } = useQuery({
    queryKey: ['billing-status'],
    queryFn: () => apiGet('/billing/status'),
  });

  // ── Checkout mutation ──
  const checkout = useMutation({
    mutationFn: ({ plan_tier, billing_cadence }) =>
      apiPost('/billing/checkout', { plan_tier, billing_cadence }),
    onSuccess: (resp) => { if (resp?.url) window.location.assign(resp.url); },
    onError: (err) => {
      setError(err.body?.detail || err.body?.error || err.message);
      setBusy(null);
    },
  });

  // ── In-place upgrade mutation ──
  const upgradeMut = useMutation({
    mutationFn: ({ plan_tier, billing_cadence }) =>
      apiPost('/billing/upgrade', { plan_tier, billing_cadence }),
    onSuccess: (_, vars) => {
      setToast({ kind: 'ok', text: `✅ Upgraded to ${vars.plan_tier} — new limits active now.` });
      queryClient.invalidateQueries({ queryKey: ['billing-status'] });
      setPendingUpgrade(null);
      const t = setTimeout(() => setToast(null), 8000);
      return () => clearTimeout(t);
    },
    onError: (err) => {
      // 400 no_subscription → push to checkout instead
      if (err.body?.error === 'no_subscription') {
        if (pendingUpgrade) {
          checkout.mutate({ plan_tier: pendingUpgrade.tier, billing_cadence: pendingUpgrade.cadence });
          setBusy(`${pendingUpgrade.tier}-${pendingUpgrade.cadence}`);
        }
      } else {
        setError(err.body?.detail || err.body?.error || err.message);
      }
      setPendingUpgrade(null);
    },
  });

  // ── Customer Portal mutation ──
  const portal = useMutation({
    mutationFn: () => apiPost('/billing/portal'),
    onSuccess: (resp) => { if (resp?.url) window.location.assign(resp.url); },
    onError: (err) => setError(err.body?.detail || err.body?.error || err.message),
  });

  const onSubscribe = (tier) => {
    setError(null);
    setBusy(`${tier}-${cadence}`);
    checkout.mutate({ plan_tier: tier, billing_cadence: cadence });
  };

  // ── Upgrade flow helpers ──
  const openUpgradeModal = (plan, cd) => {
    setError(null);
    setPendingUpgrade({
      tier:         plan.tier,
      name:         plan.name,
      cadence:      cd,
      priceMonthly: plan.priceMonthly,
      priceAnnual:  plan.priceAnnual,
    });
  };

  const confirmUpgrade = () => {
    if (!pendingUpgrade) return;
    upgradeMut.mutate({ plan_tier: pendingUpgrade.tier, billing_cadence: pendingUpgrade.cadence });
  };

  if (isLoading) return <div className="empty">Loading billing…</div>;
  if (!data) return <div className="empty">No billing info found.</div>;

  const PAID_TIERS = ['solo', 'pro', 'agency', 'starter', 'enterprise'];
  const isPaid = PAID_TIERS.includes(data.plan_tier);
  const trialEnding = data.trial_ends_at && new Date(data.trial_ends_at) > new Date();

  const postsUsed  = data.quotas.posts_used_this_month ?? 0;
  const postsCap   = data.quotas.posts_per_month ?? 0;
  const aiUsed     = data.quotas.ai_calls_used_today ?? 0;
  const aiCap      = data.quotas.ai_calls_per_day ?? 0;
  const postsPct   = postsCap > 0 ? Math.min(100, Math.round((postsUsed / postsCap) * 100)) : 0;
  const aiPct      = aiCap   > 0 ? Math.min(100, Math.round((aiUsed   / aiCap)   * 100)) : 0;

  // Plans available for in-place upgrade (strictly higher tier than current)
  const currentTierOrder = PLAN_TIER_ORDER[data.plan_tier] ?? 0;
  const upgradePlans = isPaid
    ? PLANS.filter((p) => (PLAN_TIER_ORDER[p.tier] ?? 0) > currentTierOrder)
    : [];

  return (
    <div className="account-section">
      <h2>Billing for {organization?.name}</h2>

      {toast && (
        <div className={`post-action-msg ${toast.kind === 'ok' ? 'ok' : 'err'}`} style={{ marginBottom: 16 }}>
          {toast.text}
        </div>
      )}

      {/* ── Current plan card ── */}
      <div className="billing-status-card">
        <div className="billing-status-row">
          <span className="billing-label">Current plan</span>
          <span className="billing-value">
            <span className={`plan-pill plan-${data.plan_tier}`}>{data.plan_tier}</span>
            <span className="billing-status-text">({data.status})</span>
            {data.billing_cadence && (
              <span className="billing-status-text">· {data.billing_cadence}</span>
            )}
          </span>
        </div>
        {trialEnding && (
          <div className="billing-status-row">
            <span className="billing-label">Trial ends</span>
            <span className="billing-value">{new Date(data.trial_ends_at).toLocaleDateString()}</span>
          </div>
        )}
        {data.current_period_end && (
          <div className="billing-status-row">
            <span className="billing-label">{data.cancel_at_period_end ? 'Ends' : 'Renews'}</span>
            <span className="billing-value">{new Date(data.current_period_end).toLocaleDateString()}</span>
          </div>
        )}
        {data.cancel_at_period_end && (
          <div className="billing-warn">Subscription ends at the current period end.</div>
        )}

        <hr />
        <h3>Current usage</h3>
        <div className="quota-bars">
          <QuotaBar label="Posts this month" used={postsUsed} cap={postsCap} pct={postsPct} />
          <QuotaBar label="AI calls today"   used={aiUsed}   cap={aiCap}   pct={aiPct} />
          <div className="quota-stat-row">
            <span className="billing-label">Projects</span>
            <span>{data.quotas.projects_max} max</span>
          </div>
          <div className="quota-stat-row">
            <span className="billing-label">Social accounts</span>
            <span>{data.quotas.social_accounts_max} max</span>
          </div>
        </div>

        {isPaid && data.stripe_configured && (
          <button className="btn" onClick={() => portal.mutate()} disabled={portal.isPending}
            style={{ marginTop: 16 }}>
            {portal.isPending ? 'Opening…' : 'Manage subscription (Stripe Portal)'}
          </button>
        )}
      </div>

      {/* ── AI credits wallet + top-up (ADR-0031) ── */}
      <CreditsCard />

      {/* ── Upgrade cards (paid subscriber, lower tier → higher tier) ── */}
      {isPaid && upgradePlans.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <div className="billing-plans-head">
            <h3 style={{ margin: '0 0 8px' }}>Upgrade plan</h3>
            <span style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
              Pro-rated — charged immediately for the remainder of the billing period.
            </span>
          </div>

          <div className="billing-cadence-toggle" role="tablist" style={{ margin: '12px 0 20px' }}>
            <button type="button" role="tab" aria-selected={cadence === 'monthly'}
              className={`billing-cadence-btn ${cadence === 'monthly' ? 'active' : ''}`}
              onClick={() => setCadence('monthly')}>Monthly</button>
            <button type="button" role="tab" aria-selected={cadence === 'annual'}
              className={`billing-cadence-btn ${cadence === 'annual' ? 'active' : ''}`}
              onClick={() => setCadence('annual')}>
              Annual <span className="billing-cadence-badge">−17%</span>
            </button>
          </div>

          <div className="billing-plans">
            {upgradePlans.map((plan) => {
              const priceLabel = cadence === 'annual'
                ? `$${plan.priceAnnual}/yr`
                : `$${plan.priceMonthly}/mo`;
              return (
                <div key={plan.tier} className={`billing-plan-card ${plan.recommended ? 'recommended' : ''}`}>
                  {plan.recommended && <span className="billing-plan-badge">Most popular</span>}
                  <h4>{plan.name}</h4>
                  <div className="plan-price">
                    {cadence === 'annual' ? `$${plan.priceAnnual}` : `$${plan.priceMonthly}`}
                    {' '}<span className="plan-price-sub">{cadence === 'annual' ? '/ year' : '/ month'}</span>
                  </div>
                  {cadence === 'annual' && plan.saveAnnual && (
                    <div className="plan-price-save">{plan.saveAnnual}</div>
                  )}
                  <p className="plan-blurb">{plan.blurb}</p>
                  <ul>
                    {plan.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  <button
                    className="btn btn-primary"
                    onClick={() => openUpgradeModal(plan, cadence)}
                    disabled={upgradeMut.isPending}
                  >
                    Upgrade to {plan.name} ({priceLabel})
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Subscribe (trial / no subscription) ── */}
      {!isPaid && (
        <>
          {!data.has_subscription && (
            <p className="billing-trial-note">
              Pick a plan to start your <b>14-day free trial</b> — you pay <b>$0 today</b>. Your card is
              saved at checkout and the first charge happens when the trial ends. Cancel anytime before
              then and you won't be charged.
            </p>
          )}
          <div className="billing-plans-head">
            <h3 style={{ margin: '32px 0 8px' }}>Pick a plan</h3>
            <div className="billing-cadence-toggle" role="tablist">
              <button type="button" role="tab" aria-selected={cadence === 'monthly'}
                className={`billing-cadence-btn ${cadence === 'monthly' ? 'active' : ''}`}
                onClick={() => setCadence('monthly')}>Monthly</button>
              <button type="button" role="tab" aria-selected={cadence === 'annual'}
                className={`billing-cadence-btn ${cadence === 'annual' ? 'active' : ''}`}
                onClick={() => setCadence('annual')}>
                Annual <span className="billing-cadence-badge">−17%</span>
              </button>
            </div>
          </div>

          <div className="billing-plans">
            {PLANS.map((plan) => {
              const display    = plan[cadence];
              const buttonKey  = `${plan.tier}-${cadence}`;
              return (
                <div key={plan.tier} className={`billing-plan-card ${plan.recommended ? 'recommended' : ''}`}>
                  {plan.recommended && <span className="billing-plan-badge">Most popular</span>}
                  <h4>{plan.name}</h4>
                  <div className="plan-price">
                    {display.price} <span className="plan-price-sub">{display.sub}</span>
                  </div>
                  {cadence === 'annual' && display.save && (
                    <div className="plan-price-save">{display.save}</div>
                  )}
                  <p className="plan-blurb">{plan.blurb}</p>
                  <ul>
                    {plan.features.map((f) => <li key={f}>{f}</li>)}
                  </ul>
                  <button
                    className="btn btn-primary"
                    onClick={() => onSubscribe(plan.tier)}
                    disabled={busy === buttonKey && checkout.isPending}
                  >
                    {busy === buttonKey && checkout.isPending ? 'Redirecting…' : `Subscribe to ${plan.name}`}
                  </button>
                </div>
              );
            })}
          </div>

          <p style={{ color: 'var(--color-text-muted)', fontSize: 13, marginTop: 24, lineHeight: 1.5 }}>
            Need more than 10 brands?{' '}
            <a href="mailto:sales@markaii.com?subject=Need more than 10 brands">Contact sales</a>
            {' '}— Agency extends to 15 brands with a custom contract. Above 15 we set up enterprise pricing.
          </p>
        </>
      )}

      {error && <div className="login-error" style={{ marginTop: 16 }}>{error}</div>}

      <p style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 16 }}>
        Payments are processed by Stripe. Refunds deduct the processor fee so the business breaks even by default.
      </p>

      {/* ── Upgrade confirm modal ── */}
      <UpgradeConfirmModal
        pending={pendingUpgrade}
        isPending={upgradeMut.isPending}
        currentStatus={data.status}
        onConfirm={confirmUpgrade}
        onCancel={() => setPendingUpgrade(null)}
      />
    </div>
  );
}
